/**
 * ERA→OSM mapping registry — compile-time data.
 * Mirrors mapping/era-to-osm.yaml and mapping/unmapped-rules.yaml.
 * No YAML parser required at runtime.
 */
import type { MappingRule, UnmappedRule } from '../ast/registry-types.js';

export const registryRules: MappingRule[] = [
  {
    eraEntity: 'era:OperationalPoint',
    eraKind: 'class',
    osmTargetType: 'node',
    osmTags: { railway: 'station|halt|yard|junction|crossover|spur_junction|service_station' },
    ormEndpoint: 'facility',
    matchStrategy: 'uic_ref',
    transformRule: 'Primary match on uic_ref; fall back to railway:ref, then name + distance.',
    confidence: 0.7,
    status: 'approximate',
    notes: 'ERA OP is broader than OSM station.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:SectionOfLine',
    eraKind: 'class',
    osmTargetType: 'relation',
    osmTags: { type: 'route', route: 'railway' },
    ormEndpoint: 'none',
    matchStrategy: 'relation_member',
    transformRule: 'Map to OSM route relation; approximate only.',
    confidence: 0.45,
    status: 'approximate',
    notes: 'ERA SoL granularity differs from OSM route relations.',
    sources: ['https://wiki.openstreetmap.org/wiki/Tag%3Aroute%3Drailway'],
  },
  {
    eraEntity: 'era:Track',
    eraKind: 'class',
    osmTargetType: 'way',
    osmTags: { railway: 'rail|light_rail|subway|tram|narrow_gauge' },
    ormEndpoint: 'none',
    matchStrategy: 'bbox_overpass',
    transformRule: 'Direct mapping to OSM way with railway=rail|...',
    confidence: 0.9,
    status: 'exact',
    notes: 'Strong semantic correspondence.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AKey%3Arailway'],
  },
  {
    eraEntity: 'era:RunningTrack',
    eraKind: 'class',
    osmTargetType: 'way',
    osmTags: { railway: 'rail|light_rail|narrow_gauge' },
    ormEndpoint: 'none',
    matchStrategy: 'bbox_overpass',
    transformRule: 'Like Track but exclude service=siding|spur|yard|crossover.',
    confidence: 0.65,
    status: 'approximate',
    notes: 'service= distinction is approximate.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AOpenRailwayMap/Tagging'],
  },
  {
    eraEntity: 'era:PlatformEdge',
    eraKind: 'class',
    osmTargetType: 'way',
    osmTags: { railway: 'platform', public_transport: 'platform' },
    ormEndpoint: 'none',
    matchStrategy: 'near_overpass',
    transformRule: 'Map to railway=platform. Semantic downgrade: edge → platform object.',
    confidence: 0.6,
    status: 'approximate',
    notes: 'ERA records the track-facing edge; OSM models the full platform.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AOpenRailwayMap/Tagging'],
  },
  {
    eraEntity: 'era:Tunnel',
    eraKind: 'class',
    osmTargetType: 'way',
    osmTags: { railway: 'rail|light_rail|narrow_gauge|subway|tram', tunnel: 'yes' },
    ormEndpoint: 'none',
    matchStrategy: 'near_overpass',
    transformRule: 'Track way segments with tunnel=yes.',
    confidence: 0.7,
    status: 'approximate',
    notes: 'ERA regulatory safety parameters are unmapped.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AKey%3Arailway'],
  },
  {
    eraEntity: 'era:Bridge',
    eraKind: 'class',
    osmTargetType: 'way',
    osmTags: { railway: 'rail|light_rail|narrow_gauge|subway|tram', bridge: 'yes|viaduct|movable' },
    ormEndpoint: 'none',
    matchStrategy: 'near_overpass',
    transformRule: 'Track way segments with bridge=yes|viaduct|movable.',
    confidence: 0.7,
    status: 'approximate',
    notes: 'ERA load capacity unmapped.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AKey%3Arailway'],
  },
  {
    eraEntity: 'era:ContactLineSystem',
    eraKind: 'class',
    osmTargetType: 'tag',
    osmTags: { electrified: 'yes|contact_line|rail|ground-level_power_supply', voltage: '*', frequency: '*' },
    ormEndpoint: 'none',
    matchStrategy: 'bbox_overpass',
    transformRule: 'Denormalise to flat OSM tags electrified, voltage, frequency.',
    confidence: 0.7,
    status: 'approximate',
    notes: 'ERA models this as a class; OSM as tags on the track.',
    sources: ['https://wiki.openstreetmap.org/wiki/DE%3AOpenRailwayMap/Tagging'],
  },
];

export const unmappedRules: UnmappedRule[] = [
  {
    eraEntity: 'era:trackDirection',
    category: 'unsafe-to-derive',
    description: 'OSM way direction does not reliably represent operational running direction.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:gaugingProfile',
    category: 'not-in-osm',
    description: 'TSI loading gauge profile has no standard OSM tag.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:lineCategory',
    category: 'not-in-osm',
    description: 'ERA TSI line category (P1–P6, F1–F6) has no OSM equivalent.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:etcsLevel',
    category: 'not-in-osm',
    description: 'ETCS level is a TSI parameter not in OSM.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:axleLoadCategory',
    category: 'not-in-osm',
    description: 'Axle load category has no standard OSM tag.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:trainProtectionSystem',
    category: 'not-in-osm',
    description: 'National train protection systems not in OSM.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
  {
    eraEntity: 'era:complianceInfrastructureTSI',
    category: 'not-in-osm',
    description: 'TSI compliance flag not in OSM.',
    plannerBehaviour: 'Emit warning and omit field.',
    sources: ['https://rinf.data.era.europa.eu/era-vocabulary/'],
  },
];
